<template>
  <div class="criipto-container">
    <div class="description">{{ $t('eid-redirect-description') }} {{ selectedEid.name }}.</div>
    <div class="description">{{ $t('eid-redirect-completed-description') }}</div>
    <button
      v-if="selectedEid.key === EidType.MitID"
      :class="['mitid-btn', { disabled: isDisabled }]"
      data-external="mitid-redirect-button"
      @click="handleClick"
    >
      <span class="btn-icon">
        <MitId />
      </span>
      {{ $t('sign-with-mitid') }}
    </button>
    <FriedButton
      v-else-if="selectedEid.key === EidType.BankIDFI"
      class="bankid-fi-btn"
      data-external="bankid-fi-redirect-button"
      @click="handleClick"
    >
      {{ $t('sign-with-bankid-fi') }}
    </FriedButton>
    <FriedButton
      v-else-if="selectedEid.key === EidType.NO"
      data-external="bankid-no-redirect-button"
      @click="handleClick"
    >
      {{ $t('sign-with-bankid-no') }}
    </FriedButton>
    <div v-if="status === CriiptoStatus.Error" class="error">
      {{ $t('criipto-error-message') }}
    </div>
    <div v-else-if="status === CriiptoStatus.Loading" class="loader">
      <span>{{ $t('criipto-validating-signature') }}</span>
      <FriedSpinner />
    </div>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { computed, defineComponent } from 'vue';
import { CriiptoStatus } from '../../types/enums/criipto-status';
import type { Id } from '../../types/eid';
import { EidType } from '../../types/enums/eid-type';
import MitId from '../../assets/mitid.svg';

export default defineComponent({
  components: {
    MitId,
  },
  props: {
    status: {
      type: String as PropType<CriiptoStatus>,
      required: true,
    },
    selectedEid: {
      type: Object as PropType<Id>,
      required: true,
    },
  },
  emits: ['sign'],
  setup(props, { emit }) {
    const isDisabled = computed((): boolean => [CriiptoStatus.Loading].includes(props.status));

    const handleClick = (): void => {
      emit('sign');
    };

    return { isDisabled, handleClick, CriiptoStatus, EidType };
  },
});
</script>

<style lang="scss" scoped>
$icon-size: 4rem;
$mitid-button-padding: 24px;
$mitid-button-radius: 4px;
$mitid-button-height: 42px;
$mitid-primary-colour: #0060e6;
$mitid-hover-colour: #004cb8;
$mitid-text-colour: #fff;

.criipto-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;

  .error {
    margin-top: var(--spacing-100);
  }

  .description {
    font-weight: bold;
    margin-bottom: var(--spacing-100);
  }

  .mitid-btn {
    background-color: $mitid-primary-colour;
    color: $mitid-text-colour;
    height: $mitid-button-height;
    font-weight: 600;
    border-radius: $mitid-button-radius;
    border: none;
    cursor: pointer;
    padding: $mitid-button-padding;
    transition: background-color 0.1s ease-in-out;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;

    &:hover,
    &:focus {
      background-color: $mitid-hover-colour;
    }

    &.disabled {
      background-color: var(--gray-94);
      color: var(--gray-70);
    }

    &.loading,
    &.loaded {
      background-color: var(--brand-orange);
      color: var(--white);
    }

    .btn-icon {
      width: $icon-size;
      height: $icon-size;
      margin-right: 16px;

      svg {
        width: 100%;
        height: 100%;
        max-height: $icon-size;
      }
    }
  }
}
</style>
